import React, { useState, useEffect } from 'react';
import { orderAPI, printerAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatPrice, printReceipt } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';
import {
  Package,
  Clock,
  Calendar,
  Euro,
  TrendingUp,
  ShoppingBag,
  Banknote,
  CreditCard,
  RefreshCw,
  Filter,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
  ChevronDown,
  Download,
  Search,
  Printer
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

export const OrdersHistoryPage = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('day');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [printerApiKey, setPrinterApiKey] = useState('');

  useEffect(() => {
    fetchOrders();
    // Fetch printer api key once (used by the Print buttons)
    printerAPI.getConfig().then(res => {
      if (res?.data?.printer_api_key) setPrinterApiKey(res.data.printer_api_key);
    }).catch(() => { /* no printer configured yet — fallback popup will be used */ });
  }, [period, paymentFilter, statusFilter]);

  // Deze pagina haalde alleen op bij het openen en bij het wisselen van een
  // filter. Een bestelling die in de keuken net geaccepteerd was, verscheen
  // hier dus pas na handmatig vernieuwen. Nu komt hij vanzelf binnen. Niet
  // pollen als het tabblad op de achtergrond staat: dat levert alleen verkeer
  // op dat niemand ziet.
  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) fetchOrders({ stil: true });
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, paymentFilter, statusFilter]);

  const handlePrint = async (orderId) => {
    const result = await printReceipt(orderId, printerApiKey);
    if (result.ok) {
      if (result.mode === 'rawbt') toast.success('Bon stil geprint via RawBT');
      else if (result.mode === 'browser') toast.success('Print dialoog geopend');
      else toast.success('Bon geopend in nieuw venster');
    } else {
      toast.error('Printen mislukt — controleer je printer-instellingen');
    }
  };

  // `stil` verversen betekent: geen laadbalk en geen foutmelding. Dat is wat
  // de automatische ronde hierboven gebruikt -- anders knippert de hele lijst
  // elke keer weg, en krijgt iemand die niets deed een melding bij de eerste
  // netwerkhapering.
  const fetchOrders = async ({ stil = false } = {}) => {
    if (!stil) setLoading(true);
    try {
      const params = {
        period,
        payment_method: paymentFilter || undefined,
        status: statusFilter || undefined,
        limit: 500
      };
      const response = await orderAPI.getHistory(params);
      setOrders(response.data.orders || []);
      setTotals(response.data.totals || {});
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (!stil) toast.error('Kon bestellingen niet laden');
    } finally {
      if (!stil) setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    // Hide unaccepted/failed queue orders — show only accepted, in-progress, completed and cancelled orders
    const HIDDEN_STATUSES = new Set(['pending', 'rejected', 'draft']);
    if (HIDDEN_STATUSES.has(order.status)) return false;
    if (order.payment_status === 'failed') return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.order_number?.toLowerCase().includes(query) ||
      order.customer_details?.name?.toLowerCase().includes(query) ||
      order.customer_name?.toLowerCase().includes(query) ||
      order.customer_details?.phone?.includes(query) ||
      order.customer_phone?.includes(query)
    );
  });

  const statusLabels = {
    pending: { label: 'In Wachtrij', color: 'bg-yellow-500/20 text-yellow-600' },
    confirmed: { label: 'Bevestigd', color: 'bg-blue-500/20 text-blue-600' },
    preparing: { label: 'In Bereiding', color: 'bg-purple-500/20 text-purple-600' },
    ready: { label: 'Klaar', color: 'bg-emerald-500/20 text-emerald-600' },
    out_for_delivery: { label: 'Onderweg', color: 'bg-orange-500/20 text-orange-600' },
    delivered: { label: 'Afgeleverd', color: 'bg-green-500/20 text-green-600' },
    cancelled: { label: 'Geannuleerd', color: 'bg-red-500/20 text-red-600' },
  };

  const paymentLabels = {
    cash: { label: 'Contant', icon: Banknote, color: 'text-green-600' },
    card: { label: 'Pin', icon: CreditCard, color: 'text-blue-600' },
    online: { label: 'Online', icon: CreditCard, color: 'text-purple-600' },
  };

  return (
    <div className="space-y-6" data-testid="orders-history-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alle Bestellingen</h1>
          <p className="text-gray-500 mt-1">Bekijk en filter je bestelgeschiedenis</p>
        </div>
        <Button onClick={() => fetchOrders()} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Vernieuwen
        </Button>
      </div>

      {/* Totals Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-[#1A4D2E] to-[#2d6b47] rounded-2xl p-5 text-white">
          <Euro className="w-8 h-8 text-white/80 mb-2" />
          <p className="text-3xl font-bold">{formatPrice(totals.total_revenue || 0, 'EUR')}</p>
          <p className="text-white/70 text-sm">Totale Omzet</p>
        </div>
        <div className="card-dashboard">
          <ShoppingBag className="w-8 h-8 text-[#1A4D2E] mb-2" />
          <p className="text-3xl font-bold text-gray-900">{totals.total_orders || 0}</p>
          <p className="text-gray-500 text-sm">Bestellingen</p>
        </div>
        <div className="card-dashboard">
          <Banknote className="w-8 h-8 text-green-600 mb-2" />
          <p className="text-3xl font-bold text-gray-900">{formatPrice(totals.cash_revenue || 0, 'EUR')}</p>
          <p className="text-gray-500 text-sm">Contant</p>
        </div>
        <div className="card-dashboard">
          <CreditCard className="w-8 h-8 text-purple-600 mb-2" />
          <p className="text-3xl font-bold text-gray-900">{formatPrice(totals.online_revenue || 0, 'EUR')}</p>
          <p className="text-gray-500 text-sm">Online Betaald</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card-dashboard">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-gray-700 focus:ring-2 focus:ring-[#1A4D2E]/20"
            >
              <option value="day">Vandaag</option>
              <option value="week">Deze Week</option>
              <option value="month">Deze Maand</option>
              <option value="all">Alles</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-gray-400" />
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-gray-700 focus:ring-2 focus:ring-[#1A4D2E]/20"
            >
              <option value="">Alle Betalingen</option>
              <option value="cash">Contant</option>
              <option value="online">Online</option>
              <option value="card">Pin</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-gray-700 focus:ring-2 focus:ring-[#1A4D2E]/20"
            >
              <option value="">Alle Statussen</option>
              <option value="delivered">Afgeleverd</option>
              <option value="cancelled">Geannuleerd</option>
              <option value="pending">In Wachtrij</option>
              <option value="confirmed">Bevestigd</option>
              <option value="preparing">In Bereiding</option>
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Zoek op bestelnummer, naam, telefoon..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card-dashboard overflow-hidden p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#1A4D2E] animate-spin" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Geen bestellingen gevonden</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Bestelling</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Klant</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Type</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Betaling</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Bedrag</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Datum</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredOrders.map((order) => {
                const statusInfo = statusLabels[order.status] || statusLabels.pending;
                const paymentInfo = paymentLabels[order.payment_method] || paymentLabels.cash;
                const PaymentIcon = paymentInfo.icon;
                
                return (
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => { setSelectedOrder(order); setShowDetails(true); }}
                    data-testid={`order-card-${order.id}`}
                  >
                    <td className="px-6 py-4">
                      {(() => {
                        const isDelivery = order.order_type === 'delivery';
                        const addr = order.delivery_address;
                        // For delivery: show street + house number as primary, city + postal as secondary
                        if (isDelivery && addr && (addr.street || addr.city)) {
                          const line1 = `${addr.street || ''} ${addr.house_number || ''}`.trim();
                          const line2 = `${addr.postal_code || ''} ${addr.city || ''}`.trim();
                          return (
                            <>
                              <p className="font-semibold text-gray-900 truncate max-w-[240px]" title={`${line1}, ${line2}`} data-testid={`order-primary-${order.id}`}>
                                {line1 || line2 || '—'}
                              </p>
                              <p className="text-xs text-gray-500 truncate max-w-[240px]">{line2}</p>
                              <p className="text-xs text-gray-400 mt-0.5">#{order.order_number} · {order.items?.length || 0} items</p>
                            </>
                          );
                        }
                        // For pickup / dine-in: show customer name as primary
                        const name = order.customer_details?.name || order.customer_name;
                        return (
                          <>
                            <p className="font-semibold text-gray-900 truncate max-w-[240px]" data-testid={`order-primary-${order.id}`}>
                              {name || `Bestelling #${order.order_number}`}
                            </p>
                            <p className="text-xs text-gray-500">
                              {order.order_type === 'pickup' ? 'Afhaling' : order.order_type === 'dine_in' ? 'Ter plaatse' : 'Bezorging'}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">#{order.order_number} · {order.items?.length || 0} items</p>
                          </>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-900">{order.customer_details?.name || '-'}</p>
                      <p className="text-xs text-gray-400">{order.customer_details?.phone || ''}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.order_type === 'delivery' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {order.order_type === 'delivery' ? 'Bezorging' : 'Afhalen'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <PaymentIcon className={`w-4 h-4 ${paymentInfo.color}`} />
                        <span className="text-gray-700 text-sm">{paymentInfo.label}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">{formatPrice(order.total || 0, 'EUR')}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-700 text-sm">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString('nl-BE', {
                          day: '2-digit', month: 'short'
                        }) : '-'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {order.created_at ? new Date(order.created_at).toLocaleTimeString('nl-BE', {
                          hour: '2-digit', minute: '2-digit'
                        }) : ''}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); setShowDetails(true); }}
                          className="text-gray-400 hover:text-[#1A4D2E]"
                          title="Details bekijken"
                          data-testid={`history-view-btn-${order.order_number}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handlePrint(order.id); }}
                          className="text-gray-400 hover:text-[#1A4D2E]"
                          title="Bon opnieuw printen"
                          data-testid={`history-print-btn-${order.order_number}`}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary Footer */}
      <div className="card-dashboard">
        <div className="flex flex-wrap gap-6 items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-gray-600">{totals.completed_orders || 0} Afgeleverd</span>
            </div>
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="text-gray-600">{totals.cancelled_orders || 0} Geannuleerd</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-gray-500 text-sm">Gemiddelde bestelling</p>
            <p className="text-xl font-bold text-[#1A4D2E]">
              {formatPrice(totals.total_orders > 0 ? (totals.total_revenue / totals.total_orders) : 0, 'EUR')}
            </p>
          </div>
        </div>
      </div>

      {/* Order Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#1A4D2E]" />
              {(() => {
                if (!selectedOrder) return '';
                const isDelivery = selectedOrder.order_type === 'delivery';
                const addr = selectedOrder.delivery_address;
                const primary = isDelivery && addr
                  ? `${addr.street || ''} ${addr.house_number || ''}${addr.city ? ', ' + addr.city : ''}`.trim()
                  : (selectedOrder.customer_details?.name || selectedOrder.customer_name);
                return (
                  <div className="flex-1 min-w-0">
                    <p className="text-lg font-bold truncate">{primary || `Bestelling #${selectedOrder.order_number}`}</p>
                    <p className="text-xs font-normal text-gray-500 truncate">
                      #{selectedOrder.order_number} · {isDelivery ? 'Bezorging' : selectedOrder.order_type === 'pickup' ? 'Afhaling' : 'Ter plaatse'}
                      {selectedOrder.created_at ? ` · ${new Date(selectedOrder.created_at).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}
                    </p>
                  </div>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4 py-2">
              {/* Scheduled banner */}
              {selectedOrder.scheduled_type === 'scheduled' && selectedOrder.scheduled_for && (
                <div className="border-2 border-orange-300 bg-orange-50 rounded-xl p-3 flex items-center gap-3">
                  <Clock className="w-5 h-5 text-orange-600 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-orange-700 font-medium">Geplande bestelling</p>
                    <p className="font-bold text-orange-900">
                      {new Date(selectedOrder.scheduled_for).toLocaleString('nl-BE', { weekday: 'long', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider">Klant</p>
                  <p className="text-gray-900 font-medium">{selectedOrder.customer_details?.name || selectedOrder.customer_name || '—'}</p>
                  <p className="text-gray-600 text-sm">{selectedOrder.customer_details?.phone || selectedOrder.customer_phone || ''}</p>
                  {selectedOrder.customer_email && (
                    <p className="text-gray-500 text-xs">{selectedOrder.customer_email}</p>
                  )}
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider">Status</p>
                  <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${
                    statusLabels[selectedOrder.status]?.color || 'bg-gray-100 text-gray-700'
                  }`}>
                    {statusLabels[selectedOrder.status]?.label || selectedOrder.status}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">Betaling: {paymentLabels[selectedOrder.payment_method]?.label || selectedOrder.payment_method}</p>
                </div>
              </div>

              {selectedOrder.order_type === 'delivery' && selectedOrder.delivery_address && (
                <div className="border-t pt-4">
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Bezorgadres</p>
                  <p className="text-gray-900">
                    {selectedOrder.delivery_address.street} {selectedOrder.delivery_address.house_number}<br/>
                    {selectedOrder.delivery_address.postal_code} {selectedOrder.delivery_address.city}
                  </p>
                  {selectedOrder.delivery_address.additional_info && (
                    <p className="text-gray-500 text-sm mt-1">{selectedOrder.delivery_address.additional_info}</p>
                  )}
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Items</p>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-gray-700">{item.quantity}x {item.name}</span>
                      <span className="text-gray-900 font-medium">{formatPrice((item.price || 0) * (item.quantity || 1), 'EUR')}</span>
                    </div>
                  ))}
                </div>
              </div>

              {selectedOrder.special_instructions && (
                <div className="border-t pt-4">
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Opmerkingen klant</p>
                  <p className="text-gray-800 text-sm italic">{selectedOrder.special_instructions}</p>
                </div>
              )}

              <div className="border-t pt-4 flex justify-between items-center">
                <span className="font-medium text-gray-900">Totaal</span>
                <span className="text-xl font-bold text-[#1A4D2E]">{formatPrice(selectedOrder.total || 0, 'EUR')}</span>
              </div>

              <div className="border-t pt-4 flex gap-2">
                <Button
                  onClick={() => handlePrint(selectedOrder.id)}
                  className="flex-1 gap-2 bg-[#1A4D2E] hover:bg-[#143d24] text-white"
                  data-testid="history-detail-print-btn"
                >
                  <Printer className="w-4 h-4" />
                  Bon Printen
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowDetails(false)}
                  data-testid="history-detail-close-btn"
                >
                  Sluiten
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersHistoryPage;
